import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Two-way bind a single URL search param to local input state with a debounce on writes back to
 * the URL. Reads (from initial mount and back/forward navigation) are immediate; writes (from
 * user typing) wait {@code delayMs} so we don't push a history entry for every keystroke.
 *
 * <p>Returns the local draft value (rendered on the input) plus a setter; the URL becomes the
 * source of truth after the debounce fires. Setting also resets the {@code page} param to 0 — a
 * new query starts on the first page.
 */
export const useDebouncedSearchParam = (param: string, delayMs = 300) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const initial = searchParams.get(param) ?? ''
  const [draft, setDraft] = useState(initial)

  // Sync draft when the URL changes externally (back/forward nav, programmatic replaces).
  useEffect(() => {
    setDraft(searchParams.get(param) ?? '')
  }, [searchParams, param])

  // Debounce draft → URL.
  useEffect(() => {
    const current = searchParams.get(param) ?? ''
    if (draft === current) return
    const handle = setTimeout(() => {
      const next = new URLSearchParams(searchParams)
      if (draft) {
        next.set(param, draft)
      } else {
        next.delete(param)
      }
      next.delete('page')
      setSearchParams(next, { replace: false })
    }, delayMs)
    return () => clearTimeout(handle)
  }, [draft, param, delayMs, searchParams, setSearchParams])

  return [draft, setDraft] as const
}
